import { useTranslation } from "react-i18next";
import { Building2, ChevronDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";

/**
 * Workspace switcher. Changing workspace changes what every subsequent request
 * is scoped to, so the react-query cache is cleared rather than left to go
 * stale — otherwise the previous workspace's brands flash in before refetch.
 */
export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const workspaceId = useAuthStore((s) => s.workspaceId);
  const switchWorkspace = useAuthStore((s) => s.switchWorkspace);

  const memberships = user?.memberships ?? [];
  if (memberships.length === 0) return null;

  // One workspace is the common case — show it as a label, not a dropdown.
  if (memberships.length === 1) {
    const only = memberships[0]!;
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <Building2 size={14} />
        </span>
        <span className="truncate font-medium">{only.name}</span>
      </div>
    );
  }

  return (
    <label className="relative flex items-center gap-2 rounded-xl px-1.5 py-1 text-sm transition-colors hover:bg-primary-50">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
        <Building2 size={14} />
      </span>
      <span className="sr-only">{t("workspace.switcher")}</span>
      <select
        value={workspaceId ?? ""}
        onChange={(e) => {
          switchWorkspace(e.target.value);
          qc.clear();
        }}
        className="appearance-none rounded bg-transparent pe-6 font-medium focus:outline-none focus:ring-2 focus:ring-primary-400"
      >
        {memberships.map((m) => (
          <option key={m.workspaceId} value={m.workspaceId}>
            {m.name}
          </option>
        ))}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute end-1 text-muted" />
    </label>
  );
}
